// Phase 12 — /upgrade paywall + mock checkout
import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkles, ArrowRight, CheckCircle2, ShieldCheck, Lock, Loader2, CreditCard, Cloud, Server, AlertTriangle } from 'lucide-react';
import { cn } from "@/lib/utils";
import { useBillingStore, useBillingState, useBillingDaysLeft } from "@/store/useBillingStore";
import { useAssistantName } from "@/store/useTeamStore";
import { Logo } from "@/components/common/Logo";

const PLANS = [
  { id: "starter",    Icon: Sparkles, title: "Starter",            monthly: 499,  blurb: "Just you + Maya.",          features: ["1 founder seat", "All channels (WhatsApp · Email · LinkedIn · SMS)", "100 leads / mo enrich", "Email + chat support"], recommended: true },
  { id: "pro",        Icon: Server,    title: "Pro",                monthly: 999,  blurb: "Up to 5 collaborators.",     features: ["5 seats — shared Maya", "All channels", "500 leads / mo enrich", "Priority support · 30-min SLA"] },
  { id: "enterprise", Icon: Cloud,     title: "Enterprise",         monthly: 1499, blurb: "Self-hosted · BYO KMS.",     features: ["Terraform module", "Single-tenant K8s", "Bring your own KMS", "GovCloud / EU-residency"], cta: "Deploy" },
];

export default function UpgradePage() {
  const navigate = useNavigate();
  const maya = useAssistantName();
  const [params] = useSearchParams();
  const reason = params.get("reason");

  const state = useBillingState();
  const daysLeft = useBillingDaysLeft();
  const { cardOnFile, cardLast4, upgrade, setCardOnFile } = useBillingStore();

  const [selected, setSelected] = useState("starter");
  const [processing, setProcessing] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const [card, setCard] = useState({ number: "", expiry: "", cvc: "", zip: "" });

  const plan = PLANS.find((p) => p.id === selected);

  const expired = state === "trial-expired" || reason === "trial-expired";

  let blurbText = "";
  if (expired) {
    blurbText = `Your trial has expired. Pick a plan to bring ${maya} back online.`;
  } else if (state === "trial-ending-soon") {
    blurbText = `Only ${daysLeft} day${daysLeft === 1 ? "" : "s"} left in your trial. Lock in a plan to keep things running.`;
  } else {
    blurbText = `${maya} is on a 14-day trial. Pick a plan to keep her around — cancel anytime.`;
  }

  let ctaContent;
  if (processing) {
    ctaContent = (
      <>
        <Loader2 size={13} className="animate-spin" /> Processing…
      </>
    );
  } else if (plan.toSales) {
    ctaContent = (
      <>
        {plan.cta || "Talk to sales"} <ArrowRight size={13} />
      </>
    );
  } else {
    ctaContent = (
      <>
        Subscribe to {plan.title} · ${plan.monthly}/mo <ArrowRight size={13} />
      </>
    );
  }

  const subscribe = () => {
    if (plan.toSales) { toast("Sales team will reach out within 4 business hours"); return; }
    if (!cardOnFile) { setCardOpen(true); return; }
    setProcessing(true);
    setTimeout(() => {
      upgrade(plan.id);
      setProcessing(false);
      toast.success(`Subscription active · Welcome aboard${maya ? ", " + maya + " is back online" : ""}`);
      navigate("/today");
    }, 1800);
  };

  const saveCard = () => {
    if (!card.number.trim() || !card.expiry.trim()) { toast.error("Please complete card details"); return; }
    setCardOnFile(card.number.slice(-4) || "4242");
    setCardOpen(false);
    toast.success("Card added · subscribing…");
    setProcessing(true);
    setTimeout(() => {
      upgrade(plan.id);
      setProcessing(false);
      toast.success(`Subscription active · Welcome aboard${maya ? ", " + maya + " is back online" : ""}`);
      navigate("/today");
    }, 1800);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50/40 via-white to-violet-50/30">
      <nav className="sticky top-0 z-20 bg-white/85 backdrop-blur border-b border-slate-200/70">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-2 sm:gap-4 min-w-0">
          <Link to="/today" className="shrink-0">
            <Logo size={22} />
          </Link>
          <div className="ml-auto inline-flex items-center gap-1 text-[10.5px] sm:text-[11.5px] text-slate-500 min-w-0"><Lock size={11} className="shrink-0" /> <span className="truncate">Secure checkout · 256-bit TLS</span></div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10 min-w-0" data-testid="upgrade-page">
        {expired && (
          <div className="rounded-xl bg-rose-50 ring-1 ring-rose-200 px-4 py-3 mb-5 flex items-center gap-3" data-testid="upgrade-expired-banner">
            <AlertTriangle size={16} className="text-rose-600 shrink-0" />
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-rose-900">Your trial ended. {maya} stopped working at midnight.</div>
              <div className="text-[11.5px] text-rose-700">Subscribe below to bring her back online — your data, persona, and connections are intact.</div>
            </div>
          </div>
        )}

        <div className="text-[10.5px] uppercase tracking-wider text-indigo-700 font-semibold inline-flex items-center gap-1"><Sparkles size={11} /> {expired ? "Resume your account" : "Upgrade your account"}</div>
        <h1 className="mt-2 text-[26px] sm:text-[34px] md:text-[40px] font-semibold tracking-tight text-slate-900 leading-tight">Keep {maya} working</h1>
        <p className="mt-2 text-[14px] text-slate-600 max-w-xl">{blurbText}</p>

        {/* Plans */}
        <div className="mt-7 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {PLANS.map((p) => {
            const active = selected === p.id;
            return (
              <button key={p.id} type="button" onClick={() => setSelected(p.id)}
                className={cn(
                  "relative text-left rounded-2xl border bg-white p-4 transition-all",
                  active ? "border-indigo-500 ring-2 ring-indigo-200 shadow-sm" : "border-slate-200 hover:border-slate-300",
                )}
                data-testid={`upgrade-plan-${p.id}`}>
                {p.recommended && <Badge className="absolute -top-2 right-3 bg-indigo-600 text-white text-[9.5px] tracking-wider uppercase">Recommended</Badge>}
                <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-700"><p.Icon size={15} /></div>
                <div className="mt-2 text-[14px] font-semibold text-slate-900">{p.title}</div>
                <div className="mt-1 text-[18px] font-semibold tabular-nums text-indigo-700">${p.monthly}<span className="text-[12px] text-slate-500 font-medium">/mo</span></div>
                {p.oneTime && <div className="text-[10.5px] text-slate-500">+ ${p.oneTime.toLocaleString()} one-time</div>}
                <div className="mt-1 text-[11.5px] text-slate-500 leading-snug">{p.blurb}</div>
                <ul className="mt-3 space-y-1">
                  {p.features.map((f) => <li key={f} className="text-[11px] text-slate-700 flex items-start gap-1.5"><CheckCircle2 size={11} className="text-emerald-600 mt-0.5 shrink-0" /><span>{f}</span></li>)}
                </ul>
              </button>
            );
          })}
        </div>

        {/* Card on file */}
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3" data-testid="upgrade-card-block">
          <div className="h-10 w-10 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center"><CreditCard size={16} /></div>
          <div className="flex-1 min-w-[200px]">
            {cardOnFile ? (
              <>
                <div className="text-[12.5px] font-semibold text-slate-900">Card ending in {cardLast4 || "4242"}</div>
                <div className="text-[11px] text-slate-500">Default payment method · Visa</div>
              </>
            ) : (
              <>
                <div className="text-[12.5px] font-semibold text-slate-900">No card on file</div>
                <div className="text-[11px] text-slate-500">We&apos;ll collect payment details before subscribing.</div>
              </>
            )}
          </div>
          {cardOnFile ? (
            <Button size="sm" variant="outline" className="h-10 sm:h-8 w-full sm:w-auto" onClick={() => setCardOpen(true)} data-testid="upgrade-update-card">Update</Button>
          ) : (
            <Button size="sm" variant="outline" className="h-10 sm:h-8 w-full sm:w-auto" onClick={() => setCardOpen(true)} data-testid="upgrade-add-card">Add card</Button>
          )}
        </div>

        {/* CTA */}
        <div className="mt-6 flex flex-col-reverse sm:flex-row flex-wrap items-stretch sm:items-center justify-between gap-3">
          <button type="button" onClick={() => navigate(-1)} className="text-[12.5px] text-slate-500 hover:text-slate-800 py-2 sm:py-0 text-center sm:text-left" data-testid="upgrade-think">I&rsquo;ll think about it</button>
          <Button size="lg" className="h-11 text-[13px] px-5 w-full sm:w-auto" disabled={processing} onClick={subscribe} data-testid="upgrade-subscribe">
            {ctaContent}
          </Button>
        </div>

        <div className="mt-6 text-[11px] text-slate-500 inline-flex items-center gap-1"><ShieldCheck size={11} className="text-emerald-600" /> Secure · 14-day money-back · cancel anytime</div>
      </div>

      {/* Add / Update card */}
      <Dialog open={cardOpen} onOpenChange={setCardOpen}>
        <DialogContent className="w-[calc(100vw-1.5rem)] sm:max-w-[420px]" data-testid="upgrade-card-modal">
          <DialogHeader>
            <DialogTitle>{cardOnFile ? "Update payment method" : "Add payment method"}</DialogTitle>
            <DialogDescription className="sr-only">Enter card number, expiry, CVC, and billing zip.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Input placeholder="Card number" value={card.number} onChange={(e) => setCard({ ...card, number: e.target.value })} className="h-9" data-testid="upgrade-card-number" />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="MM/YY" value={card.expiry} onChange={(e) => setCard({ ...card, expiry: e.target.value })} className="h-9" data-testid="upgrade-card-expiry" />
              <Input placeholder="CVC" value={card.cvc} onChange={(e) => setCard({ ...card, cvc: e.target.value })} className="h-9" data-testid="upgrade-card-cvc" />
            </div>
            <Input placeholder="Billing zip" value={card.zip} onChange={(e) => setCard({ ...card, zip: e.target.value })} className="h-9" data-testid="upgrade-card-zip" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCardOpen(false)}>Cancel</Button>
            <Button onClick={saveCard} data-testid="upgrade-card-save">Save &amp; subscribe</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
